import api from './api';

/**
 * How long the rodízio check may take before it counts as "not cleared". The SimpleSolution API runs on
 * the restaurant's own server: a hung one must not keep a request open, and the rodízio stays hidden
 * meanwhile, so a timeout never exposes anything.
 */
export const ALL_YOU_CAN_EAT_GATE_TIMEOUT_MS = 8000;

interface IHasRodizioResponse {
  hasAllYouCanEatEnabled?: unknown;
}

/**
 * Asks the restaurant's SimpleSolution API whether the card already has the rodízio launched
 * (`GET /cards/{cardNumber}/has-rodizio`). Resolves `true` only for an explicit boolean `true`; every
 * other outcome, including any failure, resolves `false`. It never throws: the rodízio is shown only
 * when the check says so.
 *
 * Why the shared `api` instance, with these options, and not `apiCard`:
 * - `apiCard` sends the customer to `/not-available` on any 404, so a SimpleSolution installation that
 *   does not have this endpoint yet would take the customer out of the menu;
 * - `api` redirects the whole page on 401, 404 and 5xx unless `skipErrorRedirect` is set;
 * - its automatic retries and 60 s timeout would hold the check for a long time, so both are turned
 *   off or shortened for this call only;
 * - the endpoint is anonymous, like `POST /orders/bsfood`, and the customer token means nothing to
 *   SimpleSolution, so it is not sent.
 *
 * The path is relative: `company.simpleSolutionApi` already carries the API prefix, like the other
 * SimpleSolution calls.
 */
export const getCardAllYouCanEatEnabled = async (
  baseURL: string | undefined,
  cardNumber: number,
): Promise<boolean> => {
  if (!baseURL?.trim() || !Number.isInteger(cardNumber) || cardNumber <= 0) return false;

  try {
    const { data } = await api.get<IHasRodizioResponse>(`/cards/${cardNumber}/has-rodizio`, {
      baseURL,
      skipErrorRedirect: true,
      skipAuthHeader: true,
      timeout: ALL_YOU_CAN_EAT_GATE_TIMEOUT_MS,
      'axios-retry': { retries: 0 },
    });

    return data?.hasAllYouCanEatEnabled === true;
  } catch {
    return false;
  }
};
